import { Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatIconRegistry, MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { collection, doc, setDoc, addDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { dataStore, db } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { getObjectDifferences } from 'ts-object-difference';
import { Particularity, RidePreparationLog } from 'src/app/interfaces';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export interface DialogData {
  item?: Particularity;
  isSystemParticularity?: boolean;
}

@Component({
  selector: 'app-manage-particularity',
  templateUrl: './manage-particularity.component.html',
  styleUrl: './manage-particularity.component.scss',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatTooltip,
    MatIcon,
    MatButton,
    MatError,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
    MatSlideToggle,
    MatDialogActions,
    MatDialogClose,
  ],
})
export class ManageParticularityComponent implements OnInit, OnDestroy {
  form = this.fb.group({
    name: ['', [Validators.required]],
    description: ['', [Validators.required, Validators.maxLength(40)]],
    takesSeat: [false],
    active: [true],
  });
  iconChanged: boolean = false;
  loading = signal<boolean>(true);
  saving = signal<boolean>(false);
  selectedFile: File | null = null;
  svgError = signal<string | null>(null);
  previewIconId = signal<string | null>(null);
  previewIconIds: string[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ManageParticularityComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private sanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    if (this.data.item) {
      this.form.patchValue(this.data.item);
      if (this.data.item.id) {
        this.previewIconId.set(this.data.item.id);
      }
    }

    this.loading.set(false);
  }

  ngOnDestroy() {
    // Clean up preview icons from the registry
    this.previewIconIds.forEach((iconId) =>
      (this.iconRegistry as any)._svgIconConfigs.delete(iconId),
    );
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.svgError.set(null);
      if (file.type !== 'image/svg+xml' || !file.name.toLowerCase().endsWith('.svg')) {
        this.svgError.set('Selecteer een geldig SVG-bestand.');
        event.target.value = '';
        return;
      }

      this.iconChanged = true;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const svgContent = e.target.result;

        // Parse the SVG content and remove any fills/default colors
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');

        // Remove fills from internal style blocks
        doc.querySelectorAll('style').forEach((el) => {
          if (el.textContent) {
            el.textContent = el.textContent.replace(
              /(fill|fill-opacity|stop-color|flood-color|lighting-color)\s*:\s*[^;}]+;?/gi,
              '',
            );
          }
        });

        doc.querySelectorAll('*').forEach((element) => {
          // Remove fill attributes
          element.removeAttribute('fill');
          element.removeAttribute('fill-opacity');
          element.removeAttribute('stop-color'); // For gradients
          element.removeAttribute('flood-color'); // For filters
          element.removeAttribute('lighting-color'); // For filters

          // Remove fill from inline styles
          const style = element.getAttribute('style');
          if (style) {
            const newStyle = style.replace(
              /(fill|fill-opacity|stop-color|flood-color|lighting-color)\s*:\s*[^;]+;?/gi,
              '',
            );

            if (newStyle.trim() === '') {
              element.removeAttribute('style');
            } else {
              element.setAttribute('style', newStyle);
            }
          }
        });
        const newSvgContent = new XMLSerializer().serializeToString(doc);

        const safeSvg: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(newSvgContent);
        const iconId = `temp-preview-${Date.now()}`;
        this.previewIconIds.push(iconId);
        this.iconRegistry.addSvgIconLiteral(iconId, safeSvg);
        this.previewIconId.set(iconId);

        this.selectedFile = new File([newSvgContent], file.name, { type: 'image/svg+xml' });
      };
      reader.readAsText(file);
    }
  }

  async save() {
    this.svgError.set(null);
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.previewIconId()) {
      this.svgError.set('Er is nog geen svg-bestand geselecteerd.');
      return;
    }
    if (this.saving()) {
      return;
    }
    this.saving.set(true);

    const organisationId = dataStore.selectedOrganisation.id;
    const storage = getStorage();
    let iconUrl = this.data.item?.iconUrl || null;

    try {
      if (this.iconChanged && this.selectedFile) {
        const storageRef = ref(
          storage,
          `organisations/${organisationId}/particularities/${Date.now()}-${this.selectedFile.name}`,
        );
        const snapshot = await uploadBytes(storageRef, this.selectedFile);
        iconUrl = await getDownloadURL(snapshot.ref);
      }

      const docId =
        this.data.item?.id ||
        doc(collection(db, `organisations/${organisationId}/particularities`)).id;

      const particularityData = {
        ...this.form.value,
        iconUrl,
        lastEditedAt: new Date(),
        lastEditedBy: dataStore.rootUser.email ?? dataStore.rootUser.id,
        id: docId,
      } as any;

      await setDoc(
        doc(db, `organisations/${organisationId}/particularities/${docId}`),
        particularityData,
        { merge: true },
      );

      await this.logChanges(particularityData);

      this.snackBar.open('Bijzonderheid opgeslagen.', 'X', { duration: 5000 });
      this.dialogRef.close();
    } catch (error) {
      this.snackBar.open('Er is iets misgegaan tijdens het opslaan van de bijzonderheid.', 'X', {
        duration: 5000,
      });
      this.saving.set(false);
    }
  }

  async logChanges(newObj: any) {
    const changes = getObjectDifferences(this.data.item ?? {}, newObj, {
      ignoreKeys: ['id'],
    });
    if (!changes) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: LogType.RidePreparationParticularity,
      date: new Date(),
      action: this.data.item ? 'update' : 'create',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: 'bijzonderheid',
      itemId: newObj.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      ...changes,
    };
    if (!this.data.item) {
      logObj.before = null;
    }

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }

  getError(control: string) {
    const field = this.form.get(control);
    if (field.touched || !field.pristine) {
      switch (true) {
        case field.hasError('required'):
          return 'Dit veld is verplicht.';
        case field.hasError('maxlength'):
          const maxLength = field.getError('maxlength').requiredLength;
          return `De beschrijving mag maximaal ${maxLength} tekens bevatten.`;
      }
      return '';
    }
  }

  downloadSvg() {
    if (!this.previewIconId()) {
      return;
    }
    this.iconRegistry.getNamedSvgIcon(this.previewIconId()).subscribe((svg) => {
      const svgString = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'icon.svg';
      a.click();
      URL.revokeObjectURL(url);
    });
  }
}
